<script setup lang="ts">
/**
 * Le cadre d'un dialogue du module (`ouvrirDialogue`, maquette) : un titre,
 * un corps, un pied de boutons, au-dessus de la page.
 *
 * Accessible : `role="dialog"`, `aria-modal`, titré par son en-tête ; Échap
 * ou un clic sur le fond le ferment (sauf pendant un envoi : la personne
 * attend la réponse) ; Tab et Maj+Tab restent dans le dialogue ; à la
 * fermeture, le focus revient à l'élément qui l'avait (le bouton qui l'a
 * ouvert).
 *
 * Le focus initial ne va JAMAIS à un bouton qui écrit : au premier champ
 * (saisie, case, liste), sinon à l'élément marqué `data-focus-initial`,
 * sinon au dialogue lui-même. Ainsi Entrée maintenue à l'ouverture (auto-
 * répétition du clavier) ne valide rien.
 */
// ================================
// IMPORTS
// ================================
import { nextTick, onBeforeUnmount, onMounted, ref, useId } from "vue"

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Le titre du dialogue. */
        titre: string
        /** Vrai pendant un envoi : ni Échap ni le fond ne ferment. */
        bloque?: boolean
    }>(),
    { bloque: false }
)

const emit = defineEmits<{
    /** La personne ferme le dialogue (Échap, ou le fond). */
    fermer: []
}>()

const idTitre = `dialogue-titre-${useId()}`
const boite = ref<HTMLElement | null>(null)

/** Ce qui peut prendre le focus dans le dialogue. */
const FOCALISABLES =
    'button, input, textarea, select, a[href], [tabindex]:not([tabindex="-1"])'

/** L'élément qui avait le focus avant l'ouverture, rendu à la fermeture. */
let focusAvant: HTMLElement | null = null

// ================================
// HANDLERS
// ================================
function onEchap(): void {
    if (!props.bloque) emit("fermer")
}

/** Un clic sur le fond (hors de la boîte) ferme, sauf pendant un envoi. */
function onFond(evenement: MouseEvent): void {
    if (evenement.target === evenement.currentTarget && !props.bloque)
        emit("fermer")
}

/** Tab et Maj+Tab tournent dans le dialogue. */
function onTab(evenement: KeyboardEvent): void {
    const b = boite.value
    if (!b) return
    const liste = Array.from(
        b.querySelectorAll<HTMLElement>(FOCALISABLES)
    ).filter((el) => !el.hasAttribute("disabled"))
    if (liste.length === 0) {
        evenement.preventDefault()
        b.focus()
        return
    }
    const premier = liste[0]
    const dernier = liste[liste.length - 1]
    const actif = document.activeElement
    if (evenement.shiftKey && (actif === premier || actif === b)) {
        evenement.preventDefault()
        dernier.focus()
    } else if (
        !evenement.shiftKey &&
        (actif === dernier || !b.contains(actif))
    ) {
        evenement.preventDefault()
        premier.focus()
    }
}

// ================================
// LIFECYCLE
// ================================
onMounted(async () => {
    focusAvant =
        document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null
    await nextTick()
    const b = boite.value
    if (!b) return
    const initial =
        b.querySelector<HTMLElement>("input, textarea, select") ??
        b.querySelector<HTMLElement>("[data-focus-initial]") ??
        b
    initial.focus()
})

onBeforeUnmount(() => {
    const avant = focusAvant
    focusAvant = null
    // Rendu après le retrait du dialogue du DOM.
    if (avant && avant.isConnected) void nextTick(() => avant.focus())
})
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-surface-900/40 p-4"
        data-test="dialogue-fond"
        @click="onFond"
        @keydown.esc="onEchap"
        @keydown.tab="onTab"
    >
        <div
            ref="boite"
            tabindex="-1"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="idTitre"
            class="flex max-h-full w-full max-w-xl flex-col gap-4 overflow-y-auto rounded-lg bg-surface-0 p-5 shadow-lg"
            data-test="dialogue"
        >
            <header class="flex flex-col gap-2">
                <h2
                    :id="idTitre"
                    class="text-lg font-semibold text-surface-900"
                >
                    {{ titre }}
                </h2>
                <slot name="intro" />
            </header>
            <div class="flex flex-col gap-3">
                <slot />
            </div>
            <footer class="flex flex-wrap justify-end gap-2">
                <slot name="pied" />
            </footer>
        </div>
    </div>
</template>
